import { currencyMask } from '@utils/masks/currencyMask'
import { getAccountIcon } from '@pages/ConsolidatedEntry/components/ConsolidatedGrid/helpers/getAccountIcon'

import type { ColumnDefProps } from '@components/shared/Table/types'
import type { BranchClassificationItemProps } from '@services/api/previewManagement/types'

import * as S from '../styles'

const ACCOUNT_LABEL: Record<string, string> = {
  faturamento: 'Faturamento',
  consumo: 'Consumo',
  pessoal: 'Pessoal',
  outrasDespesas: 'Outras Despesas',
  depreciacao: 'Depreciação',
  bonificacao: 'Bonificação'
}

const ACCOUNT_KEYS = ['faturamento', 'consumo', 'pessoal', 'outrasDespesas', 'depreciacao', 'bonificacao'] as const

const formatPercentage = (value: number) => `${value.toFixed(2)}%`.replace('.', ',')

const getAccountValue = (row: BranchClassificationItemProps, label: string) =>
  row.accounts.find((account) => account.account === label)?.actualPreview ?? 0

const getAccountPercentage = (row: BranchClassificationItemProps, label: string) =>
  row.accounts.find((account) => account.account === label)?.actualPreviewPercentage ?? 0

const renderAccountHeader = (accountKey: string) => {
  const account = getAccountIcon(ACCOUNT_LABEL[accountKey])

  return (
    <S.HeaderLabel>
      <S.IconWrapper color={account.color}>{account.icon}</S.IconWrapper>
      {ACCOUNT_LABEL[accountKey]}
    </S.HeaderLabel>
  )
}

export const parseUnitClassificationGrid = (
  rows: BranchClassificationItemProps[] = [],
  onOpenUnitDetails?: (unit: { branchCode: string; branchDescription: string }) => void
) => {
  const columns: ColumnDefProps[] = [
    {
      id: 'rankPosition',
      columnName: '#',
      value: '',
      headerAlign: 'center',
      bodyAlign: 'left',
      minWidth: '3rem'
    },
    {
      id: 'unit',
      columnName: 'Unidade',
      value: '',
      headerAlign: 'center',
      bodyAlign: 'left',
      minWidth: '12rem',
      sortable: true,
      sortKey: 'branchCode'
    },
    ...ACCOUNT_KEYS.map((accountKey) => ({
      id: accountKey,
      columnName: renderAccountHeader(accountKey),
      value: '',
      align: 'center' as const,
      minWidth: '9rem',
      sortable: true,
      sortKey: accountKey
    })),
    {
      id: 'grossProfit',
      columnName: 'Gross Profit (GP)',
      value: '',
      align: 'center',
      minWidth: '9rem',
      sortable: true,
      sortKey: 'grossProfit'
    },
    {
      id: 'grossMargin',
      columnName: 'Gross Margin (GM)',
      value: '',
      align: 'center',
      minWidth: '9rem',
      sortable: true,
      sortKey: 'grossMargin'
    }
  ]

  const data: ColumnDefProps[][] = rows.map((row, index) => [
    {
      ...columns[0],
      value: index + 1,
      withoutTooltip: true
    },
    {
      ...columns[1],
      value: (
        <S.UnitLabel
          clickable={!!onOpenUnitDetails}
          onClick={() => onOpenUnitDetails?.({ branchCode: row.branchCode, branchDescription: row.branchDescription })}
        >
          {row.branchDescription}
        </S.UnitLabel>
      ),
      withoutTooltip: true
    },
    ...ACCOUNT_KEYS.map((accountKey, index) => ({
      ...columns[2 + index],
      value: (
        <S.CellWrapper>
          <S.CellValue>{currencyMask(getAccountValue(row, ACCOUNT_LABEL[accountKey]))}</S.CellValue>
          <S.CellTrend>{formatPercentage(getAccountPercentage(row, ACCOUNT_LABEL[accountKey]))}</S.CellTrend>
        </S.CellWrapper>
      ),
      withoutTooltip: true
    })),
    {
      ...columns[2 + ACCOUNT_KEYS.length],
      value: <S.CellValue>{currencyMask(row.grossProfit.value)}</S.CellValue>,
      withoutTooltip: true
    },
    {
      ...columns[3 + ACCOUNT_KEYS.length],
      value: <S.GrossMarginValue>{formatPercentage(row.grossMargin.value)}</S.GrossMarginValue>,
      withoutTooltip: true
    }
  ])

  return { data }
}
